import { useMemo, useState } from 'react';
import { IFwtSummary, IRecipientForList } from '@nkr/models/index';
import { MatchStatus, MenuLocation, PatientType, RecipientAction } from '@nkr/enums/index';
import { BodyScrollEvent, CellClickedEvent, ColDef, FirstDataRenderedEvent, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IHeaderParams, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Button, Glyphicon, Label, OverlayTrigger } from 'react-bootstrap';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { GridWrapper } from './styles';
import { Utility } from '@nkr/services/utility';
import ActionButtonRenderer from './../../profile/action-button-renderer';
import PopoverTemplate from '@nkr/components/popover-template';
import Icon from 'react-icons-kit';
import { question } from 'react-icons-kit/icomoon/question';
import FwlDetailsModal from '@nkr/views/target-new/modals/fwt-details-modal';
import { useMediaQuery } from 'react-responsive';

interface IProps {
    centerId: number,
    data: IRecipientForList[],
    showPanel: boolean,
    centerHasRecipientShield: boolean,
    isAdminLogin: boolean,
    onShowFwt: (recipientId: number) => void,
    onAction: (id: number, action: RecipientAction) => void,
    onViewRegistration: (id: number) => void,
}

const Grid = (props: IProps) => {
    const [menuDisplayed, setMenuDisplayed] = useState(false);
    const isBigScreen = useMediaQuery({ minWidth: 1824 });

    const onGridReady = (e: GridReadyEvent) => {
        e.api.setSortModel([
            {
                colId: 'isExportable',
                sort: 'asc'
            }
        ]);

        e.api.sizeColumnsToFit();
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onSortChanged = (e: SortChangedEvent) => {
        e.api.refreshCells();
    };

    const onBodyScroll = (e: BodyScrollEvent) => {
        if (menuDisplayed) {
            document.body.click();
            setMenuDisplayed(false);
        }
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const handleMenuToggle = (show: boolean) => {
        setMenuDisplayed(show);
    };

    const columns: ColDef[] = [
        {
            headerName: 'R#',
            field: 'isLeadRetrieved',
            width: 65,
            sortable: true,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>R#</SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1
        },
        {
            headerName: 'Type',
            field: 'patientTypeId',
            width: 120,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Type</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRecipientForList;
                let style = 'info';

                if (data.patientTypeId == PatientType.KidneyReplacementCase) {
                    style = 'danger';
                }

                if (data.patientTypeId == PatientType.Paired) {
                    style = 'default';
                }

                if (data.patientTypeId == PatientType.Waitlist) {
                    style = 'warning';
                }

                if (data.patientTypeId == PatientType.StandardVoucherHolder || data.patientTypeId == PatientType.FamilyVoucherHolder) {
                    style = 'success';
                }

                return <Label bsStyle={style}>{data.patientTypeAbbr}</Label>;
            }
        },
        {
            headerName: 'Registered',
            field: 'registeredDate',
            width: 120,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Registered</SortableHeaderComponent>,
            comparator: Utility.dateComparator
        },
        {
            headerName: 'Alias',
            field: 'alias',
            width: 160,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Alias</SortableHeaderComponent>,
        },
        {
            headerName: 'Name',
            width: 200,
            hide: !isBigScreen,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Name</SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => {
                if (params.data.firstName == null && params.data.lastName == null) {
                    return null;
                }

                return params.data.firstName + ' ' + params.data.lastName
            }
        },
        {
            headerName: 'Email',
            field: 'email',
            width: 180,
            hide: !isBigScreen,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Email</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                return <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }} title={params.data.email}>{params.data.email}</span>;
            }
        },
        {
            headerName: 'G',
            field: 'gender',
            width: 65,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>G</SortableHeaderComponent>,
        },
        {
            headerName: 'Age',
            field: 'age',
            width: 65,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Age</SortableHeaderComponent>,
        },
        {
            headerName: 'Rec ABO',
            field: 'bloodType',
            width: 75,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>ABO</SortableHeaderComponent>,
        },
        {
            headerName: 'Intended Donor ABO',
            field: 'donorBloodTypes',
            width: 90,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Intended<br />Donor<br />ABO</SortableHeaderComponent>,
        },
        {
            headerName: 'High Res Typing',
            field: 'has3Typing',
            width: 85,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>High-<br />Res<br />Typing</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRecipientForList;
                return (
                    <div style={{ paddingTop: 3 }}>{data.has3Typing ? <Glyphicon glyph='ok-sign' /> : null}</div>
                );
            },
        },
        {
            headerName: 'NKR cPRA4',
            field: 'cpra4',
            width: 85,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>NKR<br />cPRA</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRecipientForList;
                return data.cpra4.toFixed(2);
            },
        },
        {
            headerName: 'Decline Ratio',
            field: 'declineRatio',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>
                <>Donor<br />Decline <br /> Ratio
                    &nbsp;
                    <OverlayTrigger
                        rootClose
                        placement='bottom'
                        trigger='hover'
                        overlay={<PopoverTemplate dismiss={false}>The Donor Decline Ratio reflects the higher value between a patient's current decline ratio and their historic decline ratio in preselects from the past three months.</PopoverTemplate>}
                    >
                        <Icon icon={question} size={12} style={{ color: 'blue' }} />
                    </OverlayTrigger>
                </>
            </SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRecipientForList;

                const hrefPrefix = `/center/${props.centerId}/recipient/${data.id}/profile/donor-declines?ddrtype=2`;

                return <a href={hrefPrefix}> {(data.highestDeclineRatio * 100.0).toFixed(0)}%</a>;
            },
        },
        {
            headerName: 'Dialysis Time (months)',
            field: 'dialysisTime',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Dialysis<br />Time <br /> <div style={{ fontSize: 12 }}>(months)</div></SortableHeaderComponent>,
        },
        {
            headerName: '1-Yr Mortality Risk',
            field: 'oneYearMortalityRisk',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>1-Yr <br /> Mortality <br /> Risk</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRecipientForList;
                return <span>{data.oneYearMortalityRisk ?? 0}%</span>;
            }
        },
        {
            headerName: 'Wait Actual',
            field: 'applicableWaitTime',
            width: 75,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Actual<br />Wait <div style={{ fontSize: 12 }}>(months)</div></SortableHeaderComponent>,
        },
        {
            headerName: 'Est Wait',
            field: 'estimatedWaitTime',
            width: 95,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Est<br /> Wait <div style={{ fontSize: 12 }}>(months)</div></SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRecipientForList;

                return <span style={{ color: data.estimatedWaitTime >= 60 ? 'red' : null }}>{data.estimatedWaitTime >= 60 ? '60+' : data.estimatedWaitTime.toString()}</span>
            },
        },
        {
            headerName: 'Fcst Wait',
            field: 'forecastedWaitTime',
            width: 95,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Fcst<br /> Wait <div style={{ fontSize: 12 }}>(months)</div></SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRecipientForList;

                if (data.recipientFwtString == "N/A")
                    return "N/A";
                else
                    return (
                        <Button bsStyle='link' style={{ padding: 2 }} onClick={() => props.onShowFwt(data.id)}>
                            <span style={{ color: parseInt(data.recipientFwtString) >= 60 ? 'red' : null }}>{parseInt(data.recipientFwtString) >= 60 ? '60+' : data.recipientFwtString}</span>
                        </Button>
                    );
            },
        },
        {
            headerName: 'Status',
            field: 'matchStatus',
            width: 165,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Status</SortableHeaderComponent>,
        },
        {
            headerName: 'Exportability',
            field: 'isExportable',
            width: 150,
            sortable: true,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Exportability</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRecipientForList;
                let style = 'info';
                let title = '';
                let label = '';

                if (!data.isActive && data.matchStatusId === MatchStatus.NeedsMatch) {
                    style = 'default';
                    title = 'Patient is inactive and will not be matched';
                    label = 'Inactive';
                } else if (data.matchStatusId > MatchStatus.NeedsMatch) {
                    if (data.matchStatusId === MatchStatus.Transplanted) {
                        style = 'primary';
                        title = 'Patient is transplanted';
                        label = 'Transplanted';
                    } else {
                        style = 'info';
                        title = 'Patient is already in a match';
                        label = 'In Match';
                    }
                } else {
                    if (data.isExportable && data.patientTypeId != PatientType.Waitlist) {
                        style = 'success';
                        title = 'Patient is active and all required data is completed to receive a match offer';
                        label = 'Matchable';
                    } else if (data.patientTypeId == PatientType.Waitlist) {
                        return <Label>N/A</Label>
                    } else {
                        style = 'danger';
                        title = 'Patient is active, but NOT all data required to receive a match offer has been entered';
                        label = 'Incomplete';
                    }
                }

                return <Label bsStyle={style} title={title}>{label}</Label>;
            },
            comparator: (valueA: any, valueB: any, nodeA: any, nodeB: any) => {
                const getLabel = (data: IRecipientForList) => {
                    if (!data.isActive && data.matchStatusId === MatchStatus.NeedsMatch) return 'Inactive';
                    if (data.matchStatusId === MatchStatus.Transplanted) return 'Transplanted';
                    if (data.matchStatusId > MatchStatus.NeedsMatch) return 'In Match';

                    if (data.patientTypeId == PatientType.Waitlist) {
                        return 'N/A';
                    }

                    if (data.patientTypeId != PatientType.Waitlist) {
                        return data.isExportable ? 'Matchable' : 'Incomplete';
                    }
                };

                const labelA = getLabel(nodeA.data);
                const labelB = getLabel(nodeB.data);

                const sortOrder = ['In Match', 'Matchable', 'Incomplete', 'N/A', 'Inactive'];

                return sortOrder.indexOf(labelA) - sortOrder.indexOf(labelB);
            }
        },
        // {
        //     colId: 'exportabilityCheck',
        //     headerName: 'EC',
        //     field: 'exportabilityCheck',
        //     width: 130,
        //     headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>EC<br />(debug only)</SortableHeaderComponent>,
        //     cellRendererFramework: (params: ICellRendererParams) => <>C: {params.value ? '1' : '0'}, DB: {params.data.isExportable ? 'Y' : 'N'}</>,
        // },
        {
            headerName: 'Microsite',
            field: 'micrositeRecipientId',
            width: 95,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Microsite</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRecipientForList;

                return (
                    <div style={{ paddingTop: 3 }}>{data.micrositeRecipientId > 0 ? <Glyphicon glyph='ok-sign' /> : null}</div>
                );
            },
        },
        {
            headerName: 'Pending Preselects',
            field: 'pendingBioMatchDonors',
            width: 120,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Pending<br />Preselects</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRecipientForList;

                if (data.pendingBioMatchDonors < 0) {
                    return <>--</>
                }

                return <>{params.value}</>
            }
        },
        {
            field: 'actionId',
            headerName: '',
            width: 115,
            cellClass: 'action-menu',
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRecipientForList;

                return (
                    <ActionButtonRenderer
                        {...params}
                        recipientId={data.id}
                        centerId={props.centerId}
                        hasMedicareAb={data.hasMedicareAb}
                        centerHasRecipientShield={data.centerHasRecipientShield}
                        recipientShieldEnrolled={data.recipientShieldEnrolled}
                        statusId={data.matchStatusId}
                        orderId={data.requisitionId}
                        isAdmin={false}
                        isAdminLogin={props.isAdminLogin}
                        menuLocation={MenuLocation.List}
                        recipientCenterId={data.centerId}
                        patientTypeId={data.patientTypeId}
                        voucherDonorId={data.voucherDonorId}
                        voucherDonorAlias={data.voucherDonorAlias}
                        voucherDonorCenterId={data.voucherDonorCenterId}
                        canOrderCheekSwab={data.canOrderCheekSwab}
                        canDelete={data.canDelete}
                        isWaitlistEligible={data.isWaitlistEligible}
                        micrositeRecipientId={data.micrositeRecipientId}
                        onMenuToggle={handleMenuToggle}
                        onItemSelected={props.onAction}
                    />
                )
            }
        }
    ];

    const columnDefs = useMemo(() => columns, [isBigScreen]);
    const rowData = useMemo(() => props.data, [props.data]);

    return (<>
        <GridWrapper className='ag-theme-bootstrap'>
            <AgGridReact
                defaultColDef={{
                    cellClass: 'list-cell',
                    headerClass: 'list-header',
                    sortable: true
                }}
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={58}
                groupHeaderHeight={30}
                suppressRowClickSelection={false}
                suppressCellSelection={true}
                enableCellTextSelection={true}
                suppressContextMenu={true}
                onGridReady={onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onSortChanged={onSortChanged}
                onBodyScroll={onBodyScroll}
                onGridSizeChanged={onGridSizeChanged}
                onCellClicked={(e: CellClickedEvent) => {
                    if (e.colDef.field != 'ctrAlias' && e.colDef.field != 'actionId' && e.colDef.field != 'forecastedWaitTime') {
                        let { id } = (e.data as IRecipientForList);
                        props.onViewRegistration(id);
                        e.node.setSelected(props.showPanel);
                    }
                }}
            />
        </GridWrapper>
    </>);
}

export default Grid;